import { useEffect, useState } from "react";
import { Loader2, Mail, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PackageSelect, Problem, detail, danger, field, ghost, primary } from "./ui";

const BLANK = { type: "A", host: "@", value: "", priority: "", weight: "", port: "" };
const num = (v) => (v === "" ? null : Number(v));

const AddForm = ({ r, set, submit, busy }) => (
  <div className="mt-4 grid gap-2 sm:grid-cols-[auto_1fr_2fr_auto_auto_auto_auto]">
    <select value={r.type} onChange={(e) => set({ ...r, type: e.target.value })} data-testid="dns-type-select" className={field}>{["A", "AAAA", "CNAME", "MX", "TXT", "SRV"].map((t) => <option key={t}>{t}</option>)}</select>
    <input value={r.host} onChange={(e) => set({ ...r, host: e.target.value })} placeholder="Host (@, www)" data-testid="dns-host-input" className={field} />
    <input value={r.value} onChange={(e) => set({ ...r, value: e.target.value })} placeholder="Value" data-testid="dns-value-input" className={field} />
    {["MX", "SRV"].includes(r.type) ? <input value={r.priority} onChange={(e) => set({ ...r, priority: e.target.value })} placeholder="Priority" data-testid="dns-priority-input" className={`${field} w-24`} /> : <span />}
    {r.type === "SRV" ? <input value={r.weight} onChange={(e) => set({ ...r, weight: e.target.value })} placeholder="Weight" data-testid="dns-weight-input" className={`${field} w-24`} /> : <span />}
    {r.type === "SRV" ? <input value={r.port} onChange={(e) => set({ ...r, port: e.target.value })} placeholder="Port" data-testid="dns-port-input" className={`${field} w-24`} /> : <span />}
    <button onClick={submit} disabled={busy || !r.value} data-testid="dns-add-btn" className={primary}><Plus className="h-4 w-4" />Add</button>
  </div>
);

export const DnsPanel = ({ pkgs }) => {
  const [pid, setPid] = useState("");
  const [zone, setZone] = useState(null);
  const [r, setR] = useState(BLANK);
  const [zoho, setZoho] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState(null);
  const load = (id = pid) => id && api.get(`/admin/twentyi/dns/${id}`).then((x) => setZone(x.data)).catch((e) => setFail(detail(e)));
  useEffect(() => { setZone(null); setFail(null); setPending(null); load(pid); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (req, confirm_offline = false) => {
    setBusy(true); setFail(null);
    try {
      await api.post(req.url, { ...req.body, confirm_offline });
      toast.success("DNS updated"); setPending(null); setR(BLANK); load();
    } catch (e) {
      const d = detail(e);
      if (d?.offline_warning) setPending({ ...req, offline: d.message, stage: 1 }); else { setFail(d); setPending(null); }
    }
    setBusy(false);
  };
  const add = () => send({ url: `/admin/twentyi/dns/${pid}`, body: { add: [{ type: r.type, host: r.host, value: r.value, priority: num(r.priority), weight: num(r.weight), port: num(r.port) }] } });
  const del = (rec) => setPending({ url: `/admin/twentyi/dns/${pid}`, body: { delete: [rec.ref] }, label: `Delete ${rec.type} ${rec.host} → ${rec.value}?`, stage: 0 });
  const blocked = zone?.protected;
  return (
    <div data-testid="dns-panel">
      <PackageSelect pkgs={pkgs} value={pid} onChange={setPid} testId="dns-package-select" />
      <Problem d={fail} testId="dns-error" />
      {zone && (
        <>
          {!zone.editable && <p data-testid="dns-readonly-note" className="mt-3 rounded-lg border border-amber-400/30 bg-amber-500/10 p-3 text-sm text-amber-100">Read-only here: {zone.fallback}</p>}
          <table data-testid="dns-records" className="mt-4 w-full text-left text-sm text-slate-200">
            <thead className="text-xs text-slate-500"><tr><th>Type</th><th>Host</th><th>Value</th><th>Priority</th><th /></tr></thead>
            <tbody>{zone.records.map((x, i) => (
              <tr key={`${x.type}-${x.host}-${i}`} data-testid={`dns-row-${i}`} className="border-t border-white/5">
                <td className="py-1.5">{x.type}</td><td>{x.host}</td><td className="max-w-xs truncate font-mono text-xs">{x.value}</td><td>{x.priority ?? ""}</td>
                <td>{zone.editable && !blocked && x.ref && x.type !== "NS" && <button onClick={() => del(x)} aria-label="Delete record" data-testid={`dns-delete-${i}`} className="text-red-300 hover:text-red-200"><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>))}</tbody>
          </table>
          {zone.editable && !blocked && <AddForm r={r} set={setR} submit={add} busy={busy} />}
          {zone.editable && !blocked && (
            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 p-3">
              <Mail className="h-4 w-4 text-cyan-200" /><span className="text-sm text-slate-300">Zoho Mail setup — 3 MX, SPF and the verification CNAME</span>
              <input value={zoho} onChange={(e) => setZoho(e.target.value.trim())} placeholder="zb12345678 code from Zoho" data-testid="dns-zoho-code" className={`${field} w-56`} />
              <button onClick={() => send({ url: `/admin/twentyi/dns/${pid}/zoho`, body: { code: zoho } })} disabled={busy || zoho.length < 4} data-testid="dns-zoho-btn" className={ghost}>Add Zoho records</button>
            </div>
          )}
        </>
      )}
      {pending && (
        <div data-testid="dns-confirm" className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/10 p-4 text-sm text-amber-100">
          <p className="font-semibold">{pending.stage === 0 ? pending.label : pending.stage === 1 ? pending.offline : `Really continue? ${pending.offline}.`}</p>
          <div className="mt-3 flex gap-2">
            <button disabled={busy} data-testid="dns-confirm-btn" className={danger}
              onClick={() => (pending.stage === 0 ? send(pending) : pending.stage === 1 ? setPending({ ...pending, stage: 2 }) : send(pending, true))}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{pending.stage === 2 ? "Yes, take it offline" : "Continue"}
            </button>
            <button onClick={() => setPending(null)} data-testid="dns-confirm-cancel" className={ghost}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
